<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <div class="image-selector-container">
    <div class="image-selector-wrapper" :class="{ collapsed: collapsed }">
      <div>
        <div class="card" v-for="image in images" :key="image.id" :class="{ active: alreadyAdded(image) }">
          <a class="card-image" @click="addImage(image)">
            <img :src="appendShortTermToken(imageThumbUrl(image), shortTermToken)" alt="Image thumbnail" />
          </a>
          <div class="card-content" @click="addImage(image)">
            <div style="display:flex; justify-content:space-between; align-items:center;">
              <image-name :image="image" style="flex:1; text-align:left; overflow:hidden;" />
              <b-tooltip :label="getAiStatusHint(aiStatuses[image.id])" position="is-top" type="is-dark"
                v-if="aiStatuses[image.id]">
                <div class="ai-status ml-2" style="display:flex; align-items:center; gap: 4px; cursor: help;">
                  <span class="icon is-small has-text-grey-light">
                    <i class="fas fa-robot"></i>
                  </span>
                  <span class="icon is-small has-text-warning" v-if="aiStatuses[image.id] === 'Process'">
                    <i class="fas fa-spinner fa-spin"></i>
                  </span>
                  <span class="icon is-small has-text-danger" v-else-if="aiStatuses[image.id] === 'Error'">
                    <i class="fas fa-exclamation-circle"></i>
                  </span>
                  <span class="icon is-small has-text-success" v-else-if="aiStatuses[image.id] === 'Ready'">
                    <i class="fas fa-check-circle"></i>
                  </span>
                </div>
              </b-tooltip>
            </div>
            <div class="label-container mt-1"
              v-if="project && !project.blindMode && !labelErrors[image.id] && labelDataUrls[image.id]">
              <img :src="labelDataUrls[image.id]" class="label-preview" title="Image label"
                @error="$set(labelErrors, image.id, true)">
            </div>
          </div>
        </div>

        <!-- Pagination Component -->
        <div class="pagination-wrapper mt-3 mb-3 pr-2 pl-2" v-if="nbFilteredImages > perPage">
          <b-pagination :total="nbFilteredImages" :current.sync="currentPage" :per-page="perPage" order="is-centered"
            size="is-small" icon-prev="chevron-left" icon-next="chevron-right" />
        </div>
      </div>
    </div>
    <div class="image-selector-opener" @click="collapsed = !collapsed"
      :title="collapsed ? $t('expand') : $t('collapse')">
      <i class="fas" :class="collapsed ? 'fa-angle-right' : 'fa-angle-left'"></i>
    </div>
  </div>
</template>

<script>
import { get } from '@/utils/store-helpers';
import { IMAGE_FORMAT } from '@/utils/image-utils';

import ImageName from '@/components/image/ImageName';
import { ImageInstanceCollection } from '@/api';
import _ from 'lodash';
import { appendShortTermToken } from '@/utils/token-utils.js';
import Cytomine from '@/api/cytomine';

export default {
  name: 'image-selector',
  components: {
    ImageName
  },
  data() {
    return {
      images: [],
      currentPage: 1,
      perPage: 20,
      nbFilteredImages: 0,
      loading: true,
      error: false,
      aiStatuses: {},
      labelDataUrls: {},
      labelErrors: {}
    };
  },
  computed: {
    project: get('currentProject/project'),
    shortTermToken: get('currentUser/shortTermToken'),
    activeImageGroupForViewer: get('project-tree/activeImageGroupForViewer'),
    viewerModule() {
      return this.$store.getters['currentProject/currentViewerModule'];
    },
    // imageSelectorEnabled has been replaced by collapsed state in a-layout-sider
    // The store state is now synchronized with the collapsed property
    collapsed: {
      get() {
        // Initialize from store if available, otherwise use default value
        const viewer = this.$store.getters['currentProject/currentViewer'];
        if (viewer && viewer.imageSelector !== undefined) {
          return !viewer.imageSelector;
        }
        return true; // default to collapsed
      },
      set(value) {
        // Update store when collapsed state changes
        this.$store.commit(this.viewerModule + 'setImageSelector', !value);
      }
    },
    viewerImagesIds() {
      return Object.values(this.$store.getters['currentProject/currentViewer'].images).map(image => image?.imageInstance?.id);
    }
  },
  watch: {
    currentPage() {
      this.fetchImages();
    }
  },
  methods: {
    appendShortTermToken,
    getAiStatusHint(status) {
      if (!status) return '';
      if (status === 'Process') return 'AI is currently analyzing this image.';
      if (status === 'Error') return 'An error occurred during AI analysis.';
      if (status === 'Ready') return 'AI analysis completed.';
      return `AI Status: ${status}`;
    },
    async fetchAllJobsStatus() {
      const jobsPromises = this.images.map(image =>
        Cytomine.instance.api.get(`ai-algorithm-jobs?imageId=${image.id}`)
          .then(response => {
            let jobs = response.data;
            if (response.data.collection) jobs = response.data.collection;
            else if (!Array.isArray(jobs)) jobs = [jobs];

            if (jobs && jobs.length > 0) {
              jobs.sort((a, b) => (a.created || 0) - (b.created || 0));
              let latestJob = jobs[jobs.length - 1];
              let status = latestJob.status;

              let finalStatus = 'Ready';
              if (['Processing', 'Running', 'Starting'].includes(status)) finalStatus = 'Process';
              else if (['Error', 'Failed'].includes(status)) finalStatus = 'Error';

              this.$set(this.aiStatuses, image.id, finalStatus);
            }
          })
          .catch(err => {
            console.error("Failed to fetch jobs for image ", image.id, err);
          })
      );
      await Promise.all(jobsPromises);
    },
    async fetchAllLabelImages() {
      // Skip if blinded
      if (this.project && this.project.blindMode) return;

      // Revoke old URLs
      Object.values(this.labelDataUrls).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
      this.labelDataUrls = {};
      this.labelErrors = {};

      const labelPromises = this.images.map(image => this.fetchLabelImage(image));
      await Promise.all(labelPromises);
    },
    async fetchLabelImage(image) {
      try {
        const url = `imageinstance/${image.id}/associated/label.jpg`;
        const { data } = await Cytomine.instance.api.get(url, { responseType: 'blob' });
        const objectUrl = URL.createObjectURL(data);
        this.$set(this.labelDataUrls, image.id, objectUrl);
      } catch (error) {
        this.$set(this.labelErrors, image.id, true);
      }
    },
    async addImage(image) {
      try {
        // 检查图像是否已经添加到查看器中
        let existingImageIndex = this.viewerImagesIds.indexOf(image.id);
        if (existingImageIndex !== -1) {
          // 如果图像已经存在，切换到该图像
          let imageIndexes = Object.keys(this.$store.getters['currentProject/currentViewer'].images);
          let imageIndex = imageIndexes.find(index =>
            this.$store.getters['currentProject/currentViewer'].images[index].imageInstance.id === image.id
          );
          this.$store.commit(this.viewerModule + 'setActiveImage', imageIndex);
        } else {
          // 如果图像不存在，替换当前活动图像
          await image.fetch(); // refetch image to ensure we have latest version
          let slice = await image.fetchReferenceSlice();
          let activeImageIndex = this.$store.getters['currentProject/currentViewer'].activeImage;
          await this.$store.dispatch(`${this.viewerModule}images/${activeImageIndex}/setImageInstance`, { image, slices: [slice] });
        }
      } catch (error) {
        console.log(error);
        // this.$notify({type: 'error', text: this.$t('notif-error-add-viewer-image')});
      }
    },
    async fetchImages(loading = true) {
      console.log('[DEBUG] ImageSelector: Fetching images. Active group from store:', this.activeImageGroupForViewer);

      if (loading) {
        this.loading = true;
      }

      try {
        let collection;
        let baseParams = {
          nbPerPage: this.perPage,
          sort: 'id',
          order: 'asc'
        };

        if (this.activeImageGroupForViewer) {
          console.log('[DEBUG] ImageSelector: Fetching by IMAGEGROUP:', this.activeImageGroupForViewer);
          collection = new ImageInstanceCollection({
            ...baseParams,
            filterKey: 'imagegroup',
            filterValue: this.activeImageGroupForViewer,
          });
        }
        else {
          console.log('[DEBUG] ImageSelector: Fetching by PROJECT:', this.project.id);
          collection = new ImageInstanceCollection({
            ...baseParams,
            filterKey: 'project',
            filterValue: this.project.id,
          });
        }

        let data = (await collection.fetchPage(this.currentPage - 1));
        this.images = data.array;
        this.nbFilteredImages = data.totalNbItems;
        this.fetchAllJobsStatus();
        this.fetchAllLabelImages();
      } catch (error) {
        console.log(error);
        this.error = true;
      }
      if (loading) {
        this.loading = false;
      }
    },

    toggle() {
      this.collapsed = !this.collapsed;
    },

    shortkeyHandler(key) {
      if (key === 'toggle-add-image') {
        this.toggle();
      }
    },

    alreadyAdded(image) {
      return this.viewerImagesIds.includes(image.id);
    },
    imageThumbUrl(image) {
      return image.thumbURL(256, IMAGE_FORMAT);
    }
  },
  async created() {
    this.loading = true;
    await this.fetchImages(false);
    this.loading = false;
  },
  mounted() {
    this.$eventBus.$on('shortkeyEvent', this.shortkeyHandler);
  },
  beforeDestroy() {
    this.$store.dispatch('project-tree/setActiveImageGroupForViewer', null);
    this.$eventBus.$off('shortkeyEvent', this.shortkeyHandler);
    // Revoke all Object URLs
    Object.values(this.labelDataUrls).forEach(url => {
      if (url) URL.revokeObjectURL(url);
    });
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';

/* 定义滚动条样式 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: $dark-wapper-bg !important;
}

::-webkit-scrollbar-thumb {
  background: $dark-scrollbar-thumb !important;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: $dark-scrollbar-thumb-hover !important;
}

.image-selector-container {
  display: flex;
  height: 100%;
}

.image-selector-wrapper {
  background-color: $dark-wapper-bg;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
  display: flex;
  flex-direction: column;
  width: 8em;
  height: 100%;
  z-index: 150;
  overflow-y: auto;
  overflow-x: hidden;
  border-right: 1px solid $dark-border-color;
  transition: width 0.3s ease, min-width 0.3s ease, padding 0.3s ease;
}

.image-selector-wrapper.collapsed {
  width: 0;
  min-width: 0;
  border-right: none;
  overflow: hidden;
}

.image-selector-opener {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.2rem;
  background-color: $dark-bg-secondary;
  border-right: 1px solid $dark-border-color;
  cursor: pointer;
  color: $dark-text-secondary;
  z-index: 151;
}

.image-selector-opener:hover {
  background-color: $dark-bg-tertiary;
  color: $dark-text-primary;
}

.card {
  margin: 0.25em;
}

.card-image {
  display: block;
  width: 100%;
  background-color: transparent;
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
  /* Ensure the image corners are rounded */
}

.card-image img {
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
}

.card-content {
  padding: 0.5em;
  font-size: 0.9rem;
  overflow: hidden;
  text-align: center;
}

.pagination-wrapper {
  border-top: 1px solid $dark-border-color;
  padding-top: 0.75rem;
}

.label-container {
  width: 100%;
  padding-top: 0.2rem;
}

.label-preview {
  object-fit: contain;
  width: 100%;
  max-height: 128px;
  height: auto;
  display: block;
  margin: 0 auto;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
}
</style>
